<template>
  <div
    class="pass-history-view v3-pass-history behaviour-point-history-tab"
    data-testid="behaviour-point-history-tab"
  >
    <h2 class="sr-only">{{ behaviourTerminology.pointHistoryTab }}</h2>

    <div class="row g-3 mb-3 behaviour-point-history-stats">
      <div
        v-for="card in statCards"
        :key="card.key"
        class="col-12"
        :class="statColumnClass"
      >
        <BehaviourManageStatCard
          :label="card.label"
          :value="card.value"
          :icon-class="card.iconClass"
          :show-info="card.showInfo"
          :info-aria-label="card.infoAriaLabel"
          :test-id="'behaviour-point-history-stat-' + card.key"
        />
      </div>
    </div>

    <div
      class="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3"
    >
      <div class="d-flex flex-wrap align-items-center gap-2">
        <span class="text-muted small">{{ displayDateRange }}</span>
      </div>
      <div
        v-if="showStaffActions"
        class="d-flex flex-wrap align-items-center justify-content-end gap-2"
      >
        <BaseButton
          rounded
          type="button"
          data-testid="behaviour-point-history-award"
          @click="openAwardModal"
        >
          {{ $t("behaviour_manage.action_award") }}
        </BaseButton>
        <BaseButton
          rounded
          type="button"
          data-testid="behaviour-point-history-correction"
          @click="openPointCorrectionModal"
        >
          {{
            $t("behaviour_manage.action_point_correction", {
              point: behaviourTerminology.point
            })
          }}
        </BaseButton>
        <BaseButton
          solid
          rounded
          type="button"
          data-testid="behaviour-point-history-redeem"
          @click="openRedeemModal"
        >
          {{ $t("behaviour_manage.action_redeem") }}
        </BaseButton>
      </div>
    </div>

    <ActionPanel class="mb-3 behaviour-point-history-action-panel">
      <template #left-content>
        <div class="behaviour-point-history-filters w-100 min-w-0">
          <div class="behaviour-point-history-date-range-row mb-3">
            <CustomSelectDatePicker
              v-model="state.filterParams.dates"
              class="behaviour-point-history-date-range-picker w-50"
              :date-ranges="optionalDateRanges"
              :selected-school-year-limit="schoolYearForDatePicker"
              :should-persist-value="true"
              input-id="behaviour-point-history-date-range"
              :accessible-label="$t('common.date_range')"
              :placeholder="$t('common.select')"
              data-testid="behaviour-point-history-date-range"
              @update:model-value="onDateRangeChange"
            />
          </div>
          <div
            class="d-flex flex-wrap align-items-end gap-3 behaviour-point-history-toolbar"
          >
            <div class="behaviour-point-history-search-field min-w-0">
              <label
                id="behaviour-point-history-search-label"
                class="v3-form-label d-block mb-1"
                for="behaviour-point-history-search"
              >
                {{ $t("common.search") }}
              </label>
              <InputField
                class="behaviour-point-history-search"
                prepend-icon="ri-search-line"
                :model-value="state.searchInput"
                :placeholder="$t('common.search')"
                input-type="search"
                id="behaviour-point-history-search"
                data-testid="behaviour-point-history-search"
                @update:model-value="onSearchInput"
              />
            </div>
            <div class="behaviour-point-history-core-value-field min-w-0">
              <label
                id="behaviour-point-history-core-value-label"
                class="v3-form-label d-block mb-1"
                for="behaviour-point-history-core-value-input"
              >
                {{ behaviourTerminology.coreValue }}
              </label>
              <BehaviourCoreValueLazySelect
                :model-value="state.filterParams.coreValueId"
                class="behaviour-point-history-core-value-filter w-100"
                hide-floating-label
                labelled-by="behaviour-point-history-core-value-label"
                :prepend-items="coreValueFilterPrependItems"
                :selected-option="state.coreValueFilterSelectedOption"
                status-filter="active"
                :prefetch-on-mount="false"
                input-id="behaviour-point-history-core-value-input"
                append-dropdown-to-body
                append-dropdown-fixed-position
                data-testid="behaviour-point-history-core-value-filter"
                @update:model-value="onCoreValueFilterChange"
                @option-selected="onCoreValueFilterOptionSelected"
              />
            </div>
            <div
              v-if="!isStudentView"
              class="behaviour-point-history-grad-year-field min-w-0"
            >
              <label
                id="behaviour-point-history-grad-year-label"
                class="v3-form-label d-block mb-1"
                for="behaviour-point-history-grad-year-input"
              >
                {{ $t("common.grad_year") }}
              </label>
              <CustomSelect
                v-if="state.gradYears"
                v-model="state.filterParams.byGradYear"
                class="behaviour-point-history-grad-year-filter w-100"
                hide-floating-label
                labelled-by="behaviour-point-history-grad-year-label"
                :close-on-select="true"
                :multiple="true"
                :options="state.gradYears"
                :searchable="false"
                :placeholder="$t('common.by_grad_year')"
                input-id="behaviour-point-history-grad-year-input"
                data-testid="behaviour-point-history-grad-year-filter"
                @update:model-value="onGradYearChange"
              />
            </div>
          </div>
        </div>
      </template>
      <template #right-content>
        <div
          class="d-flex align-items-end flex-wrap justify-content-end gap-2 behaviour-point-history-filter-actions"
        >
          <button
            type="button"
            class="btn btn-link d-flex align-items-center p-0 text-primary text-decoration-none"
            :disabled="!hasActiveFilters || state.isLoading"
            data-testid="behaviour-point-history-reset-filters"
            @click="resetFilters"
          >
            <i class="ri-filter-off-line" aria-hidden="true" />
            <span class="ms-1 text-decoration-underline">{{
              $t("behaviour_settings.reset_filters")
            }}</span>
          </button>
          <IconButton
            v-if="showStaffActions"
            class="h-25"
            :icon="
              state.isExportLoading ? 'ri-loader-4-line' : 'ri-download-2-line'
            "
            :class="{ loading: state.isExportLoading }"
            :disabled="state.isExportLoading || state.isLoading"
            :aria-label="$t('common.download_export')"
            data-testid="behaviour-point-history-download"
            @click="downloadPointHistory"
          />
        </div>
      </template>
    </ActionPanel>

    <Loader :is-processing="state.isLoading" />

    <PerPage
      v-if="state.pagination.total > 0"
      :pagination="paginationDisplay"
      :showing-entries="true"
      @on-page-change="setPerPage"
    />

    <DataTable
      class="datatable bg-white text-center mt-3"
      add-table-classes="behaviour-point-history-datatable"
      :fields="tableFields"
      :items="state.tableRows"
      :sorter="{ external: true, resetable: false }"
      :sorter-value="sorterValue"
      :no-items-view="noItemsView"
      data-testid="behaviour-point-history-table"
      @update:sorter-value="onSortColumnChange"
    >
      <template #activity="{ item }">
        <td class="text-start">{{ activityLabel(item) }}</td>
      </template>
      <template #coreValueName="{ item }">
        <td class="text-start align-middle">
          <span
            v-if="item.coreValueId || item.coreValueName"
            class="behaviour-core-value-dot me-2"
            :class="indicatorDotClass(item)"
            :style="indicatorDotStyle(item)"
            aria-hidden="true"
          />
          <span
            v-if="item.coreValueId || item.coreValueName"
            class="visually-hidden"
          >
            {{ coreValueIndicatorAriaLabel(item) }}
          </span>
          {{ item.coreValueName }}
        </td>
      </template>
      <template #studentName="{ item }">
        <td class="text-start">{{ item.studentName }}</td>
      </template>
      <template #points="{ item }">
        <td class="text-start behaviour-point-history-points-cell">
          <span
            class="behaviour-point-history-points"
            :class="pointsClass(item)"
          >
            <span class="behaviour-point-history-points__layout">
              <span class="behaviour-point-history-points__value">
                {{ formatPoints(item.points) }}
                <span v-if="isRedeemTransaction(item)" class="visually-hidden">
                  {{ $t("behaviour_manage.transaction_type_redeem") }}
                </span>
              </span>
              <span
                class="behaviour-point-history-points__trailing"
                aria-hidden="true"
              >
                <i
                  v-if="isRedeemTransaction(item)"
                  class="ri-gift-line behaviour-point-history-points__redeem-icon"
                />
              </span>
            </span>
          </span>
        </td>
      </template>
      <template #occurredAt="{ item }">
        <td class="text-start">{{ item.occurredAt }}</td>
      </template>
      <template #awardedBy="{ item }">
        <td class="text-start">{{ item.awardedBy }}</td>
      </template>
      <template #notes="{ item }">
        <td class="text-center behaviour-point-history-notes-cell">
          <BehaviourPointHistoryNotesIcon
            :notes="item.notes"
            :row-id="item.id"
          />
        </td>
      </template>
    </DataTable>

    <Pagination
      v-if="paginationDisplay.pages > 1"
      :active-page="state.pagination.activePage"
      :pages="paginationDisplay.pages"
      class="cs-pagination mt-4"
      :dots="false"
      :double-arrows="true"
      size="lg"
      align="center"
      @update:active-page="setActivePage"
    />
  </div>
</template>

<script>
import { useGradeYears } from "@/composables/useGradeYears.js"
import { usePbisPointsSocketRefresh } from "@/composables/usePbisPointsSocketRefresh.js"
import download from "@/helpers/downloadCSV.js"
import { buildPaginationFromApiResponse } from "@/helpers/behaviourCoreValueApi.js"
import {
  formatPointHistoryApiDate,
  isPointHistoryCorrectionTransaction,
  isPointHistoryRedeemTransaction,
  mapPointHistorySortKey
} from "@/helpers/behaviourPointHistoryApi.js"
import behaviourConstants from "@/constants/behaviourManagementConstants.js"
import {
  indicatorAriaColorFor,
  indicatorDotClassFor,
  indicatorDotStyleFor
} from "@/helpers/behaviourCoreValueIndicator.js"
import {
  buildBehaviourPointHistoryDateRanges,
  getDefaultBehaviourPointHistoryDateRange,
  parseBehaviourPointHistoryDateRange
} from "@/helpers/behaviourPointHistoryFilters.js"
import BehaviourCoreValueLazySelect from "@/v3components/Behaviour/BehaviourCoreValueLazySelect.vue"
import BehaviourManageStatCard from "@/v3components/Behaviour/BehaviourManageStatCard.vue"
import BehaviourPointHistoryNotesIcon from "@/v3components/Behaviour/BehaviourPointHistoryNotesIcon.vue"
import BehaviourAwardPointsForm from "@/v3components/Forms/Behaviour/BehaviourAwardPointsForm.vue"
import BehaviourPointCorrectionForm from "@/v3components/Forms/Behaviour/BehaviourPointCorrectionForm.vue"
import BehaviourRedeemRewardForm from "@/v3components/Forms/Behaviour/BehaviourRedeemRewardForm.vue"
import ActionPanel from "@/v3components/shared/DataTable/ActionPanel.vue"
import BaseButton from "@/v3components/shared/Buttons/BaseButton.vue"
import IconButton from "@/v3components/shared/Buttons/IconButton.vue"
import DataTable from "@/v3components/shared/DataTable/DataTable.vue"
import InputField from "@/v3components/shared/Form/InputField.vue"
import Loader from "@/v3components/shared/Loader/Loader.vue"
import CustomSelect from "@/v3components/shared/Form/CustomSelect.vue"
import CustomSelectDatePicker from "@/v3components/shared/Form/CustomSelectDatePicker.vue"
import Pagination from "@/v3components/shared/DataTable/Pagination.vue"
import PerPage from "@/v3components/shared/DataTable/Perpage.vue"
import { computed, inject, onMounted, onUnmounted, reactive, watch } from "vue"
import { useI18n } from "vue-i18n"
import { useStore } from "vuex"

export default {
  name: "BehaviourPointHistoryTab",
  components: {
    ActionPanel,
    BaseButton,
    IconButton,
    BehaviourCoreValueLazySelect,
    BehaviourManageStatCard,
    BehaviourPointHistoryNotesIcon,
    CustomSelect,
    CustomSelectDatePicker,
    DataTable,
    InputField,
    Loader,
    Pagination,
    PerPage
  },
  setup() {
    const SEARCH_DEBOUNCE_MS = 320
    const { t } = useI18n()
    const store = useStore()
    const modal = inject("modal")

    const behaviourTerminology = computed(
      () => store.getters["behaviourSettings/behaviourTerminology"]
    )

    const userRole = computed(() => store.getters["authentication/getUserRole"])

    const isStudentView = computed(() => userRole.value === "student")

    const isAdminView = computed(() =>
      behaviourConstants.BEHAVIOUR_ADMIN_ROLES.includes(userRole.value)
    )

    const showStaffActions = computed(() => !isStudentView.value)

    const statColumnClass = computed(() => {
      if (isStudentView.value) return "col-sm-6 col-lg-4"
      if (isAdminView.value) return "col-sm-6 col-xl-3"
      return "col-sm-6"
    })

    const statCards = computed(() => {
      const bt = behaviourTerminology.value
      const stats = state.stats

      if (isStudentView.value) {
        return [
          {
            key: "total-earned",
            label: bt.statTotalPointsEarned,
            value: stats.totalPointsEarned,
            iconClass: "ri-medal-line",
            showInfo: false
          },
          {
            key: "used-rewards",
            label: bt.statPointsUsedOnRewards,
            value: stats.pointsUsedOnRewards,
            iconClass: "ri-gift-line",
            showInfo: false
          },
          {
            key: "remaining",
            label: bt.statRemainingPoints,
            value: stats.remainingPoints,
            iconClass: "ri-star-line",
            showInfo: false
          }
        ]
      }

      const all = [
        {
          key: "school-awarded",
          label: bt.statSchoolWidePointsAwarded,
          value: stats.schoolWideAwarded,
          iconClass: "ri-star-line",
          showInfo: false,
          adminOnly: true
        },
        {
          key: "school-redeemed",
          label: bt.statSchoolWidePointsRedeemed,
          value: stats.schoolWideRedeemed,
          iconClass: "ri-gift-line",
          showInfo: false,
          adminOnly: true
        },
        {
          key: "you-awarded",
          label: bt.statPointsYouAwarded,
          value: stats.pointsYouAwarded,
          iconClass: "ri-star-line",
          showInfo: false,
          adminOnly: false
        },
        {
          key: "you-redeemed",
          label: bt.statPointsRedeemed,
          value: stats.pointsRedeemed,
          iconClass: "ri-gift-line",
          showInfo: false,
          adminOnly: false
        }
      ]
      return isAdminView.value ? all : all.filter((card) => !card.adminOnly)
    })

    const { gradeYears, currentSchoolYear, getMinDate, getMaxDate } =
      useGradeYears()

    const schoolYearForDatePicker = computed(
      () => currentSchoolYear.value || gradeYears.value?.[0] || null
    )

    const coreValueFilterPrependItems = computed(() => [
      { label: t("behaviour_settings.status_all"), value: "all" }
    ])

    const createEmptyStats = () => ({
      schoolWideAwarded: 0,
      schoolWideRedeemed: 0,
      pointsYouAwarded: 0,
      pointsRedeemed: 0,
      totalPointsEarned: 0,
      pointsUsedOnRewards: 0,
      remainingPoints: 0
    })

    let searchDebounceId = null

    const state = reactive({
      isLoading: false,
      isExportLoading: false,
      searchInput: "",
      gradYears: [],
      tableRows: [],
      stats: createEmptyStats(),
      filterParams: {
        coreValueId: "all",
        byGradYear: [],
        dates: ""
      },
      coreValueFilterSelectedOption: coreValueFilterPrependItems.value[0],
      sortColumn: null,
      sortOrder: null,
      pagination: {
        activePage: 1,
        total: 0,
        pages: 0,
        from: 0,
        to: 0,
        per_page: { label: "25", value: 25 }
      }
    })

    const optionalDateRanges = computed(() =>
      buildBehaviourPointHistoryDateRanges(
        schoolYearForDatePicker.value,
        getMinDate,
        getMaxDate
      )
    )

    const activeDateRange = computed(() =>
      parseBehaviourPointHistoryDateRange(state.filterParams.dates)
    )

    const defaultDateRange = computed(() =>
      getDefaultBehaviourPointHistoryDateRange(
        schoolYearForDatePicker.value,
        getMinDate,
        getMaxDate
      )
    )

    const tableFields = computed(() => {
      const bt = behaviourTerminology.value
      const fields = [
        {
          key: "activity",
          label: t("behaviour_manage.col_activity"),
          sorter: true,
          _style: "width:18%;"
        },
        {
          key: "coreValueName",
          label: bt.coreValue,
          sorter: true,
          _style: "width:16%;"
        }
      ]

      if (!isStudentView.value) {
        fields.push({
          key: "studentName",
          label: t("behaviour_manage.col_to"),
          sorter: false,
          _style: "width:14%;"
        })
      }

      fields.push(
        {
          key: "points",
          label: bt.points,
          sorter: true,
          _style: "width:10%; min-width:5.75rem;",
          _classes: "behaviour-point-history-points-col"
        },
        {
          key: "occurredAt",
          label: t("behaviour_manage.col_date_time"),
          sorter: true,
          _style: "width:14%;"
        },
        {
          key: "awardedBy",
          label: t("behaviour_manage.col_from"),
          sorter: false,
          _style: "width:14%;"
        },
        {
          key: "notes",
          label: t("behaviour_manage.col_notes"),
          sorter: false,
          _style: "width:6%;"
        }
      )

      return fields
    })

    const sorterValue = computed(() => ({
      column: state.sortColumn || "",
      asc: state.sortOrder !== "desc"
    }))

    const coreValueIndicatorAriaLabel = (item) =>
      t("behaviour_settings.indicator_a11y", {
        color: indicatorAriaColorFor(item)
      })

    const displayDateRange = computed(() => {
      const dates = String(state.filterParams.dates ?? "").trim()
      if (!dates) return ""
      return dates.replace(/\s*-\s*/g, " – ")
    })

    const paginationDisplay = computed(() => state.pagination)

    const hasActiveFilters = computed(() => {
      const hasSearch = Boolean(String(state.searchInput ?? "").trim())
      const hasCoreValue =
        String(state.filterParams.coreValueId ?? "") !== "all"
      const hasGradYear =
        !isStudentView.value && Boolean(state.filterParams.byGradYear?.length)
      const hasDateRange =
        String(state.filterParams.dates ?? "").trim() !==
        String(defaultDateRange.value ?? "").trim()
      return hasSearch || hasCoreValue || hasGradYear || hasDateRange
    })

    const noItemsView = computed(() => ({
      noItems: t("behaviour_manage.no_point_history_rows", {
        point: behaviourTerminology.value.point
      })
    }))

    const formatPoints = (points) => {
      const n = Number(points) || 0
      if (n > 0) return `+${n}`
      return String(n)
    }

    const pointsClass = (item) => {
      const n = Number(item?.points) || 0
      if (n > 0) return "behaviour-point-history-points--positive"
      if (n < 0) return "behaviour-point-history-points--negative"
      return ""
    }

    const isRedeemTransaction = (item) => isPointHistoryRedeemTransaction(item)

    const isCorrectionTransaction = (item) =>
      isPointHistoryCorrectionTransaction(item)

    const activityLabel = (item) => {
      if (isRedeemTransaction(item)) {
        return t("behaviour_manage.activity_reward_redeemed")
      }
      if (isCorrectionTransaction(item)) {
        return t("behaviour_manage.activity_point_correction", {
          point: behaviourTerminology.value.point
        })
      }
      return item?.activity ?? ""
    }

    const buildTransactionQuery = () => {
      const range = activeDateRange.value
      return {
        fromDate: formatPointHistoryApiDate(range.start),
        toDate: formatPointHistoryApiDate(range.end),
        page: state.pagination.activePage,
        perPage: state.pagination.per_page?.value || 25,
        search: state.searchInput,
        gradYears: isStudentView.value ? [] : state.filterParams.byGradYear,
        coreValueId: state.filterParams.coreValueId,
        sortBy: mapPointHistorySortKey(state.sortColumn),
        sortOrder: state.sortOrder
      }
    }

    const downloadPointHistory = async () => {
      const query = buildTransactionQuery()
      if (!query.fromDate || !query.toDate || state.isExportLoading) {
        return
      }

      try {
        state.isExportLoading = true
        const exportResult = await store.dispatch(
          "behaviourManage/exportPointHistoryTransactions",
          query
        )
        if (exportResult?.url) {
          await download.downloadFromUrl(
            exportResult.url,
            exportResult.fileName || "point-history"
          )
        } else if (exportResult?.csvContent) {
          download.CSVExport(exportResult.csvContent, "point-history")
        }
      } catch {
        // Export errors are surfaced by the global axios handler when applicable.
      } finally {
        state.isExportLoading = false
      }
    }

    const fetchPointHistoryStats = async () => {
      if (isStudentView.value) {
        try {
          const summary = await store.dispatch(
            "behaviourManage/fetchStudentPointsSummary"
          )
          state.stats.totalPointsEarned = summary?.totalPointsEarned ?? 0
          state.stats.pointsUsedOnRewards = summary?.pointsUsedOnRewards ?? 0
          state.stats.remainingPoints = summary?.remainingPoints ?? 0
        } catch {
          state.stats.totalPointsEarned = 0
          state.stats.pointsUsedOnRewards = 0
          state.stats.remainingPoints = 0
        }
        return
      }

      const tasks = [
        store
          .dispatch("behaviourManage/fetchMyPointsSummary")
          .then((summary) => {
            state.stats.pointsYouAwarded = summary?.pointsAwarded ?? 0
            state.stats.pointsRedeemed = summary?.pointsRedeemed ?? 0
          })
          .catch(() => {
            state.stats.pointsYouAwarded = 0
            state.stats.pointsRedeemed = 0
          })
      ]

      if (isAdminView.value) {
        tasks.push(
          store
            .dispatch("behaviourManage/fetchSchoolWidePointsSummary")
            .then((summary) => {
              state.stats.schoolWideAwarded = summary?.pointsAwarded ?? 0
              state.stats.schoolWideRedeemed = summary?.pointsRedeemed ?? 0
            })
            .catch(() => {
              state.stats.schoolWideAwarded = 0
              state.stats.schoolWideRedeemed = 0
            })
        )
      }

      await Promise.all(tasks)
    }

    const fetchTransactions = async () => {
      const query = buildTransactionQuery()
      if (!query.fromDate || !query.toDate) {
        return
      }

      const perPageLabel =
        state.pagination.per_page?.label || String(query.perPage)
      const perPageValue = Number(query.perPage) || 25

      try {
        state.isLoading = true
        const { rows, pagination } = await store.dispatch(
          "behaviourManage/fetchPointHistoryTransactions",
          query
        )
        state.tableRows = rows || []
        state.pagination = buildPaginationFromApiResponse(pagination, {
          perPageLabel,
          perPageValue
        })
        state.pagination.per_page = {
          label: perPageLabel,
          value: perPageValue
        }
      } catch {
        state.tableRows = []
        state.pagination = {
          ...state.pagination,
          total: 0,
          pages: 0,
          from: 0,
          to: 0
        }
      } finally {
        state.isLoading = false
      }
    }

    const onSearchInput = (value) => {
      state.searchInput = value || ""
      if (searchDebounceId) {
        clearTimeout(searchDebounceId)
      }
      searchDebounceId = setTimeout(() => {
        state.pagination.activePage = 1
        fetchTransactions()
      }, SEARCH_DEBOUNCE_MS)
    }

    const onCoreValueFilterChange = (value) => {
      state.filterParams.coreValueId = value ?? "all"
      state.pagination.activePage = 1
      fetchTransactions()
    }

    const onCoreValueFilterOptionSelected = (option) => {
      state.coreValueFilterSelectedOption = option ? { ...option } : null
    }

    const onGradYearChange = () => {
      state.pagination.activePage = 1
      fetchTransactions()
    }

    const onDateRangeChange = () => {
      state.pagination.activePage = 1
      fetchTransactions()
    }

    const initializeFilters = () => {
      state.filterParams.byGradYear = []
      state.filterParams.dates = defaultDateRange.value
      state.filterParams.coreValueId = "all"
      state.coreValueFilterSelectedOption = coreValueFilterPrependItems.value[0]
    }

    const hydrateGradYearsForFilter = () => {
      const cached = store.getters["passBlocks/gradeYears"]
      if (cached?.length) {
        state.gradYears = cached
        return
      }

      store
        .dispatch("passBlocks/getGradeYears")
        .then((result) => {
          state.gradYears = result?.data?.data || []
        })
        .catch(() => {
          state.gradYears = []
        })
    }

    const resetFilters = () => {
      if (state.isLoading) return
      state.searchInput = ""
      state.sortColumn = null
      state.sortOrder = null
      initializeFilters()
      state.pagination.activePage = 1
      if (searchDebounceId) {
        clearTimeout(searchDebounceId)
        searchDebounceId = null
      }
      fetchTransactions()
    }

    const onSortColumnChange = (sorterState) => {
      const column = String(sorterState?.column ?? "").trim()
      if (!column) {
        state.sortColumn = null
        state.sortOrder = null
      } else if (mapPointHistorySortKey(column)) {
        state.sortColumn = column
        state.sortOrder = sorterState?.asc ? "asc" : "desc"
      } else {
        return
      }
      state.pagination.activePage = 1
      fetchTransactions()
    }

    const setPerPage = (option) => {
      if (!option) return
      state.pagination.per_page = option
      state.pagination.activePage = 1
      fetchTransactions()
    }

    const setActivePage = (page) => {
      state.pagination.activePage = page
      fetchTransactions()
    }

    const openRedeemModal = () => {
      modal?.open?.(BehaviourRedeemRewardForm, {
        size: "lg",
        title: t("behaviour_manage.redeem_reward_title"),
        props: {}
      })
    }

    const openPointCorrectionModal = () => {
      modal?.open?.(BehaviourPointCorrectionForm, {
        size: "lg",
        title: t("behaviour_manage.point_correction_title", {
          point: behaviourTerminology.value.point
        }),
        props: {}
      })
    }

    const openAwardModal = () => {
      modal?.open?.(BehaviourAwardPointsForm, {
        size: "lg",
        title: t("behaviour_manage.award_points_title", {
          points: behaviourTerminology.value.points
        }),
        props: {}
      })
    }

    watch(
      () => modal?.options?.action,
      (action) => {
        if (
          action === "pointHistoryRedeemed" ||
          action === "pointHistoryCorrected" ||
          action === "pointHistoryAwarded"
        ) {
          if (modal?.options) {
            modal.options.action = ""
            modal.options.actionData = null
          }
          state.pagination.activePage = 1
          fetchTransactions()
          fetchPointHistoryStats()
        }
      }
    )

    usePbisPointsSocketRefresh({
      refresh: () =>
        Promise.all([fetchTransactions(), fetchPointHistoryStats()])
    })

    onMounted(async () => {
      if (!isStudentView.value) {
        hydrateGradYearsForFilter()
      }
      initializeFilters()
      await Promise.all([fetchTransactions(), fetchPointHistoryStats()])
    })

    onUnmounted(() => {
      if (searchDebounceId) {
        clearTimeout(searchDebounceId)
        searchDebounceId = null
      }
    })

    return {
      behaviourTerminology,
      isStudentView,
      showStaffActions,
      statCards,
      statColumnClass,
      displayDateRange,
      schoolYearForDatePicker,
      coreValueFilterPrependItems,
      optionalDateRanges,
      state,
      tableFields,
      paginationDisplay,
      hasActiveFilters,
      noItemsView,
      formatPoints,
      pointsClass,
      isRedeemTransaction,
      activityLabel,
      indicatorDotClass: indicatorDotClassFor,
      indicatorDotStyle: indicatorDotStyleFor,
      coreValueIndicatorAriaLabel,
      onSearchInput,
      onCoreValueFilterChange,
      onCoreValueFilterOptionSelected,
      onGradYearChange,
      onDateRangeChange,
      resetFilters,
      onSortColumnChange,
      sorterValue,
      downloadPointHistory,
      setPerPage,
      setActivePage,
      openRedeemModal,
      openPointCorrectionModal,
      openAwardModal
    }
  }
}
</script>
